import { calculateCardWidth } from '../../../components/miscUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  addTemplate,
  deleteTemplate,
  modifyTemplate,
  modifyTemplates,
  selectTemplatesAndCombinedOntology,
  sortTemplatesByRecency,
} from '../../../data/templateSlice';
import { promptYamlModal } from '../../../components/yaml-editor/YamlEditorModal';
import { useMemo, useState } from 'react';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import styles from '../Settings.module.scss';
import { templateScopeMapper, TpTemplateInfo } from '../../../api/models/assets';
import { useApiContext } from '../../../api';
import { useAppDispatch, useAppSelector, useUser } from '../../../app/hooks';
import useResizeObserver from '../../visual-analytics/hooks/resize-observer';
import { getTemplateTypeById } from '../../../json-schemas/templateTypesFactory';
import { modifyOpenedAsset } from '../../../data/assets/queriedAssetsSlice';
import { promptConfirmationBox, TpConfirmData } from '../../../components/message-boxes/ConfirmBox';
import { isModifiableTemplateScope } from '../../../components/miscUtils';
import { FORM_DATATYPES, promptAutoForm, TpAutoFormDef } from '../../../components/auto-form-modal/AutoForm';
import { TemplateCard } from './TemplateCard';
import { confirmTemplateDeletion } from './templateDeleteConfirmation';

interface TemplateOverViewProps {
  scopeValue: string;
  refTemplateGrid: any;
  selectedTemplateTypeTag: any;
  setSelectedTemplateTypeTag: (selectedTemplateTypeValue: string) => void;
}

/** Flattens a `{ [templateType]: TpTemplateInfo[] }` map into a single array of templates. */
function flattenTemplatesByType(
  templatesByType: { [templateType: string]: TpTemplateInfo[] } | undefined,
): TpTemplateInfo[] {
  if (!templatesByType) return [];
  return Object.values(templatesByType).flat();
}

export const TemplateOverview = (props: TemplateOverViewProps) => {
  const { scopeValue, refTemplateGrid, selectedTemplateTypeTag, setSelectedTemplateTypeTag } = props;

  const api = useApiContext();
  const dispatch = useAppDispatch();
  const { user } = useUser();
  const { templates, ontology } = useAppSelector(selectTemplatesAndCombinedOntology);

  const [cardWidth, setCardWidth] = useState(220);

  // Templates for the currently selected scope/type, ordered by recency (most recently updated first).
  const filteredAssetTemplates = useMemo<TpTemplateInfo[]>(() => {
    const templatesByType = templates[scopeValue];
    if (!templatesByType) return [];
    const templatesForType = selectedTemplateTypeTag
      ? templatesByType[selectedTemplateTypeTag]
      : flattenTemplatesByType(templatesByType);
    return sortTemplatesByRecency(templatesForType ?? []);
  }, [templates, scopeValue, selectedTemplateTypeTag]);

  const filteredInvalidTemplates = useMemo<TpTemplateInfo[]>(() => {
    const invalidTemplatesByType = templates.invalidTemplates?.[scopeValue];
    if (!invalidTemplatesByType) return [];
    return selectedTemplateTypeTag
      ? (invalidTemplatesByType[selectedTemplateTypeTag] ?? [])
      : flattenTemplatesByType(invalidTemplatesByType);
  }, [templates, scopeValue, selectedTemplateTypeTag]);

  /**
   * Saves the edited YAML content for a template, either as an asset-level template override
   * (when `template.assetId` is set) or as a regular organization-level template.
   * In both cases the redux store is updated with the server's response, so `updatedAt`
   * (and therefore ordering) reflects server truth rather than a locally-constructed guess.
   */
  const savetoDb = (template: TpTemplateInfo, newContent: string) => {
    if (template.assetId) {
      const newTemplate = { ...template, yamlString: newContent };
      api.asset.modifyAssetTemplate(template.assetId, newTemplate).then((updated_asset_info) => {
        dispatch(modifyOpenedAsset({ asset: updated_asset_info }));
      });
    } else {
      api.template.modifyTemplate(template.id!, { yamlString: newContent }).then((updatedTemplate) => {
        dispatchModifyTemplate(updatedTemplate);
      });
    }
  };

  const handleToggleActive = (template: TpTemplateInfo) => {
    const action = template.isActive ? 'deactivate' : 'activate';
    const confirmData: TpConfirmData = {
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} ${template.name}`,
      description: `Do you really want to ${action} ${template.name}? \n\n`,
    };

    promptConfirmationBox(confirmData).then((confirmed) => {
      if (confirmed) {
        api.template.modifyTemplate(template.id!, { isActive: !template.isActive }).then((updatedTemplate) => {
          dispatchModifyTemplate(updatedTemplate);
        });
      }
    });
  };

  const handleSetDefault = (template: TpTemplateInfo) => {
    const confirmData: TpConfirmData = {
      title: `Set ${template.name} as default`,
      description: `Do you really want to set ${template.name} as the new default for ${template.templateType}? \n\n`,
    };

    promptConfirmationBox(confirmData).then((confirmed) => {
      if (confirmed) {
        api.template.setDefaultTemplate(template.id!).then((updatedTemplates) => {
          dispatch(modifyTemplates({ templates: updatedTemplates }));
        });
      }
    });
  };

  const handleCreateTemplate = () => {
    const allAssetTypesChoices = Object.values(ontology.assetTypes).map((assetOntology: any) => {
      return { id: assetOntology.identifier, content: assetOntology.displayName };
    });

    const form: TpAutoFormDef = {
      title: `Create new ${scopeValue} template`,
      fields: [
        {
          id: 'name',
          name: 'Internal Name',
          dataType: FORM_DATATYPES.STRING,
          startContent: '',
          required: true,
        },
        {
          id: 'displayName',
          name: 'Display Name',
          dataType: FORM_DATATYPES.STRING,
          startContent: '',
          required: true,
        },
        {
          id: 'version',
          name: 'Version',
          dataType: FORM_DATATYPES.STRING,
          startContent: '0.1.0',
          required: true,
        },
        {
          id: 'assetType',
          name: 'Asset Type',
          dataType: FORM_DATATYPES.MULTIPLECHOICE,
          startContent: '',
          choices: allAssetTypesChoices,
          required: true,
        },
        {
          id: 'isActive',
          name: 'Active',
          dataType: FORM_DATATYPES.CHECKBOX,
          startContent: false,
        },
      ],
    };
    promptAutoForm(form)
      .then((dataForm: any) => {
        const newTemplate: TpTemplateInfo = {
          name: dataForm.name,
          displayName: dataForm.displayName,
          version: dataForm.version,
          scope: templateScopeMapper(scopeValue),
          templateType: dataForm.assetType,
          yamlString: '',
          isActive: dataForm.isActive,
          isDefault: false,
          isSystem: false,
        };
        api.template.addTemplate(newTemplate).then((createdTemplate) => {
          dispatch(addTemplate({ template: createdTemplate }));
        });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useResizeObserver(refTemplateGrid, () => {
    calculateCardWidth(refTemplateGrid, styles.templateCard, 180, 350, setCardWidth);
  });

  function handleDeleteTemplate(template: TpTemplateInfo) {
    const templatesForType = templates[scopeValue]?.[template.templateType] ?? [];
    const invalidTemplatesForType = templates.invalidTemplates?.[scopeValue]?.[template.templateType] ?? [];

    return confirmTemplateDeletion(template, {
      scopeValue,
      isSystemAdmin: user.isSystemAdmin ?? false,
      templatesForType,
      allTemplatesForType: [...templatesForType, ...invalidTemplatesForType],
      listAssets: api.asset.listAssets,
      promptConfirmationBox,
      showErrorMessage,
    }).then((confirmed) => {
      if (confirmed) {
        api.template
          .deleteTemplate(template.id!)
          .then(() => {
            dispatch(deleteTemplate({ template }));
          })
          .catch((error) => {
            console.error(error);
            if (error.response && error.response.status === 400) {
              showErrorMessage('Cannot delete template because it is in use by one or more assets');
            } else {
              showErrorMessage(error.message);
            }
          });
      }
    });
  }

  function handleEditTemplate(template: TpTemplateInfo) {
    const schema = getTemplateTypeById(template.scope, template.templateType).yamlSchema;
    promptYamlModal(template.yamlString, schema).then((yaml) => {
      typeof template !== 'string' && savetoDb(template, yaml);
    });
  }

  function handleModifyTemplateMetadata(template: TpTemplateInfo) {
    const form: TpAutoFormDef = {
      title: `Modify template metadata for ${template.displayName || template.name}`,
      fields: [
        {
          id: 'displayName',
          name: 'Display Name',
          dataType: FORM_DATATYPES.STRING,
          startContent: template.displayName || template.name,
          required: true,
        },
        {
          id: 'version',
          name: 'Version',
          dataType: FORM_DATATYPES.STRING,
          startContent: template.version || '',
        },
      ],
    };

    promptAutoForm(form).then((dataForm: any) => {
      api.template
        .modifyTemplate(template.id!, { displayName: dataForm.displayName, version: dataForm.version })
        .then((updatedTemplate) => {
          dispatchModifyTemplate(updatedTemplate);
        });
    });
  }

  function dispatchModifyTemplate(template: TpTemplateInfo) {
    try {
      dispatch(modifyTemplate({ template }));
    } catch (error) {
      console.error(error);
      showErrorMessage('Error fetching templates data. \n Please contact your administrator.');
      return;
    }
  }

  return (
    <>
      <div className={styles.templateGrid} ref={refTemplateGrid}>
        {filteredInvalidTemplates.map((template: TpTemplateInfo) => (
          <TemplateCard
            key={template.id}
            template={template}
            scopeValue={scopeValue}
            ontology={ontology}
            cardWidth={cardWidth}
            isInvalid
            isModifiable={isModifiableTemplateScope(scopeValue)}
            isSystemAdmin={user.isSystemAdmin ?? false}
            onEdit={handleEditTemplate}
            onModifyMetadata={handleModifyTemplateMetadata}
            onDelete={handleDeleteTemplate}
            onToggleActive={handleToggleActive}
            onSetDefault={handleSetDefault}
          />
        ))}
        {filteredAssetTemplates.map((template: TpTemplateInfo) => (
          <TemplateCard
            key={template.id}
            template={template}
            scopeValue={scopeValue}
            ontology={ontology}
            cardWidth={cardWidth}
            isInvalid={false}
            isModifiable={isModifiableTemplateScope(scopeValue)}
            isSystemAdmin={user.isSystemAdmin ?? false}
            onEdit={handleEditTemplate}
            onModifyMetadata={handleModifyTemplateMetadata}
            onDelete={handleDeleteTemplate}
            onToggleActive={handleToggleActive}
            onSetDefault={handleSetDefault}
          />
        ))}
      </div>
      {isModifiableTemplateScope(scopeValue) && (
        <div
          className={styles.addButton}
          onClick={(ev) => {
            ev.stopPropagation();
            handleCreateTemplate();
          }}
        >
          <FontAwesomeIcon icon={['fas', 'plus']} />
        </div>
      )}
    </>
  );
};

export default TemplateOverview;
